import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEyeSlash, faShuffle, faTrophy } from '@fortawesome/free-solid-svg-icons'
import cx from 'classnames'
import React, { ChangeEvent, createRef, useEffect, useState } from 'react'

import {
  addOrUpdateGuestPlayer,
  switchActiveBlaster,
} from '../../../actions/social/leaderboard-actions'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import settingsSlice from '../../../reducers/settingsSlice'
import { selectIsOwnedMatchSlug } from '../../../selectors/blaster-peer-selectors'
import { selectCurrentGamers } from '../../../selectors/current-play-selectors'
import {
  selectMatchStatsByPlayer,
  TrackPlayerCell,
  TrackPlayerData,
  TrackPlayerRow,
} from '../../../selectors/leaderboard-selectors'
import { selectScoreVersion, selectCurrentUsername } from '../../../selectors/session-selectors'
import { selectIsHidingLeaderboardResults } from '../../../selectors/settings-selectors'
import { GuestPlayer, ScoreBonuses } from '../../../types'
import { TextInputWithLabel } from '../../widgets/TextInputWithLabel'
import TrackBadge from '../../widgets/TrackBadge'
import GuestPlayerSetup from '../match-info/GuestPlayerSetup'

type Props = { matchOwner: string; matchSlug: string; playlistSlug: string; isPrintReady?: boolean }

const StatsByPlayerTable = ({
  matchOwner,
  matchSlug,
  playlistSlug,
  isPrintReady = false,
}: Props) => {
  const dispatch = useAppDispatch()
  const compoundMatchSlug = `${matchOwner}/${matchSlug}`
  const isRootOwnedMatch = useAppSelector(selectIsOwnedMatchSlug(compoundMatchSlug))
  const currUsername = useAppSelector(selectCurrentUsername)
  const isOwnedMatch = matchOwner === currUsername
  const isLeaderboardMode = !playlistSlug
  const [workingPlayerOrder, setWorkingPlayerOrder] = useState<string[]>([])
  const trackPlayerData: TrackPlayerData = useAppSelector(
    selectMatchStatsByPlayer(matchOwner, matchSlug, playlistSlug, workingPlayerOrder)
  )
  const {
    colLabels,
    rowLabels,
    dataRows,
    playerCounts: { guestCount },
  } = trackPlayerData

  const gamers = useAppSelector(selectCurrentGamers)
  const scoreVersion = useAppSelector(selectScoreVersion)
  const isDefaultToHiddenInfo = useAppSelector(selectIsHidingLeaderboardResults)
  const activeGamerIds = gamers.filter(({ isActive }) => isActive).map(({ gamerId }) => gamerId)
  const [isShowBonuses, setIsShowBonuses] = useState(false)
  const [playlistVizState, setPlaylistVizState] = useState<{ [key: string]: boolean }>({})
  const [indexToSwitchTo, setIndexToSwitchTo] = useState(0)
  const [selectedPlayerInfo, setSelectedPlayerInfo] = useState<GuestPlayer | null>(null)
  const [playerHideInfoState, setPlayerHideInfoState] = useState<boolean[]>(
    rowLabels.map(() => isDefaultToHiddenInfo)
  )
  const [isHideMatchInfo, setIsHideMatchInfo] = useState(isDefaultToHiddenInfo)

  useEffect(() => {
    const initialPlaylistState = playlistSlug ? { [playlistSlug]: true } : {}
    setPlaylistVizState(initialPlaylistState)
    setWorkingPlayerOrder([])
  }, [playlistSlug])

  const playerNameEditRef = createRef<HTMLInputElement>()
  React.useEffect(() => {
    const elem = playerNameEditRef.current
    if (elem) {
      elem.select()
    }
  })

  const getBonuses = (bonuses?: ScoreBonuses) => {
    if (!bonuses) {
      return null
    }
    const { hiddenLyrics, randomTrack } = bonuses
    return (
      <div className="bonusButtons">
        {!!randomTrack && (
          <button>
            <FontAwesomeIcon size="sm" icon={faShuffle} title={String(randomTrack)} />
          </button>
        )}
        {!!hiddenLyrics && (
          <button>
            <FontAwesomeIcon icon={faEyeSlash} title={String(hiddenLyrics)} size="sm" />
          </button>
        )}
      </div>
    )
  }

  const onGuestOrderChange = (newOrder: string[]) => {
    const compoundPlayerSlugs = newOrder.map((playerId) => {
      return `${matchOwner}/${playerId}`
    })
    setWorkingPlayerOrder(compoundPlayerSlugs)
  }
  const toggleAllPlayerInfo = (isToggleOn: boolean) => {
    setPlayerHideInfoState((prevState) => {
      return prevState.map(() => isToggleOn)
    })
    dispatch(settingsSlice.actions.setIsHidingLeaderboardResults(isToggleOn))
  }
  const toggleMatchInfo = () => {
    if (!isLeaderboardMode) {
      setIsHideMatchInfo(!isHideMatchInfo)
    }
  }
  const togglePlaylistViz = (playlistToToggle: string) => {
    const newState =
      playlistToToggle in playlistVizState ? !playlistVizState[playlistToToggle] : true
    const newPlaylistVizState = { ...playlistVizState }
    newPlaylistVizState[playlistToToggle] = newState
    setPlaylistVizState(newPlaylistVizState)
  }
  const getRowLabel = (rowIndex: number) => {
    const { title, slug: compoundPlayerSlug } = rowLabels[rowIndex]
    const [username, playerSlug] = compoundPlayerSlug.split('/')
    const blasterIndex = activeGamerIds.indexOf(playerSlug)
    const isActive = blasterIndex >= 0
    const blastButtonClassname = cx('blastButton', {
      mini: true,
      left: blasterIndex === 0,
      inactive: !isActive,
    })
    const onTogglePlayerInfoHidden = () => {
      if (!isLeaderboardMode) {
        return
      }
      setPlayerHideInfoState((prevState) => {
        const newState = [...prevState]
        const newRowHiddenState = !prevState[rowIndex]
        newState[rowIndex] = newRowHiddenState
        const allRowsNowHaveSameState = !newState.includes(!newRowHiddenState)
        if (allRowsNowHaveSameState) {
          dispatch(settingsSlice.actions.setIsHidingLeaderboardResults(newRowHiddenState))
        }
        return newState
      })
    }
    const onSwitchToPlayer = () => {
      if (isLeaderboardMode && isOwnedMatch && username === currUsername) {
        setSelectedPlayerInfo({ slug: compoundPlayerSlug, name: title })
        return
      }
      if (isActive) {
        return
      }
      dispatch(switchActiveBlaster({ gamerIndex: indexToSwitchTo, newBlasterSlug: playerSlug }))
      setIndexToSwitchTo((indexToSwitchTo + 1) % activeGamerIds.length)
    }
    const onNameChange = (event: ChangeEvent<HTMLInputElement>) => {
      const newName = event.target.value
      if (selectedPlayerInfo) {
        const { name, slug } = selectedPlayerInfo
        if (newName !== name) {
          const isDelete = !newName
          const isRenamingOrDeleteConfirmed = !isDelete || window.confirm(`Delete ${name}?`)
          if (isRenamingOrDeleteConfirmed) {
            if (isDelete && isActive) {
              alert("Can't delete active player")
            } else {
              const player = { slug, name: newName }
              dispatch(addOrUpdateGuestPlayer({ compoundMatchSlug: compoundMatchSlug, player }))
            }
          }
        }
        setSelectedPlayerInfo(null)
      }
    }
    return (
      <td className="sticky-label">
        <div className="label-wrapper">
          <div className="playerButtonWrapper">
            {
              <button
                className="matchRank"
                onClick={onTogglePlayerInfoHidden}
                title={compoundPlayerSlug}
              >
                {rowIndex + 1}
              </button>
            }
            {!isLeaderboardMode && (
              <button className={blastButtonClassname} onClick={onSwitchToPlayer} />
            )}
          </div>
          <div
            className={cx('playerScore', {
              isHidden: isLeaderboardMode && playerHideInfoState[rowIndex],
            })}
          >
            {selectedPlayerInfo?.slug === compoundPlayerSlug ? (
              <TextInputWithLabel
                label={''}
                value={title}
                onBlur={onNameChange}
                onRef={playerNameEditRef}
              />
            ) : (
              <div className={badgeClass} onClick={onSwitchToPlayer}>
                {title}
              </div>
            )}
          </div>
        </div>
      </td>
    )
  }
  const badgeClass = cx('playerBadge', { guestName: guestCount > 0 })
  const cornerClass = cx('sticky-header', 'corner', { isClickable: isLeaderboardMode })
  const getHeader = () => {
    let showTracks = false
    return (
      <tr>
        <th className={cornerClass}>
          <div className="wrapper">
            <button
              className={isShowBonuses ? '' : 'bonusOff'}
              onClick={() => {
                setIsShowBonuses(!isShowBonuses)
              }}
            >
              <FontAwesomeIcon
                size="sm"
                icon={faTrophy}
                title={`${isShowBonuses ? 'Hide' : 'Show'} Bonuses`}
              />
            </button>
            <GuestPlayerSetup
              compoundMatchSlug={compoundMatchSlug}
              isAllowNewPlayer={isLeaderboardMode}
              onGuestOrderChange={isLeaderboardMode ? undefined : onGuestOrderChange}
              onPlayersToggle={isLeaderboardMode ? toggleAllPlayerInfo : undefined}
              isPrintReady={isPrintReady || isRootOwnedMatch}
            />
          </div>
        </th>
        {colLabels.map((columnLabel, index) => {
          const { trackInfo, title } = columnLabel
          const isTrack = !!trackInfo
          if (!isTrack) {
            showTracks = playlistVizState[columnLabel.slug]
          } else if (!showTracks) {
            return null
          }
          const isMatchColumn = index === colLabels.length - 1
          const className = cx('sticky-header', {
            isMatch: isMatchColumn,
            isRound: !isTrack && !isMatchColumn,
            isClickable: !isLeaderboardMode,
          })
          return (
            <th
              key={index}
              className={className}
              onClick={() => {
                if (isTrack) {
                  setIsShowBonuses(!isShowBonuses)
                  // toggleColumnCounters(columnLabel.slug)
                } else if (isMatchColumn) {
                  toggleMatchInfo()
                } else {
                  togglePlaylistViz(columnLabel.slug)
                }
              }}
            >
              <TrackBadge trackInfo={trackInfo} title={title} scoreVersion={scoreVersion} />
            </th>
          )
        })}
      </tr>
    )
  }
  const getBody = () => {
    return dataRows.map((row, rowIndex) => {
      let showTracks = false
      const isHidePlayerInfo = playerHideInfoState[rowIndex]
      const playerRow: TrackPlayerRow = []
      row.forEach((column, colIndex) => {
        const isTrack = !!colLabels[colIndex].trackInfo
        if (!isTrack) {
          showTracks = playlistVizState[column.slug]
        } else if (!showTracks) {
          return
        }
        column.trackInfo = colLabels[colIndex].trackInfo
        playerRow.push(column)
      })
      return (
        <tr key={rowIndex}>
          {getRowLabel(rowIndex)}
          {playerRow.map(
            ({ slug, score, scoreRank, trackInfo, bonuses }: TrackPlayerCell, colIndex: number) => {
              const isMatchColumn = colIndex === playerRow.length - 1
              const isHidden = isLeaderboardMode
                ? isHidePlayerInfo
                : isMatchColumn && isHideMatchInfo
              return (
                <td key={colIndex}>
                  <div
                    className={cx('playerScore', {
                      isRound: !trackInfo,
                      isMatch: isMatchColumn,
                      isHidden,
                    })}
                  >
                    {score > 0 && (
                      <div
                        className={cx('score', { [scoreRank || '']: true })}
                        title={`${bonuses ? bonuses.scoredBlue || '' : ''}`}
                      >
                        {score}
                      </div>
                    )}
                    {isShowBonuses && getBonuses(bonuses)}
                  </div>
                </td>
              )
            }
          )}
        </tr>
      )
    })
  }

  return (
    <div className="statsByPlayerTable">
      <div className="table-container">
        <table className="table" cellPadding={0}>
          <thead>{getHeader()}</thead>
          <tbody>{getBody()}</tbody>
        </table>
      </div>
    </div>
  )
}

export default StatsByPlayerTable
